import { commonComponents as cc } from "@/components/common";
import { ui } from "@patient-watch/ui";
import { useLogoutMutation } from "@/features/auth/api/useLogoutMutation";
import DeleteUserButton from "@/features/auth/components/DeleteUserButton";
import { profileComponents as pc } from "@/features/profile/components";
import {
    ClinicianNumberDialog,
    DateOfBirthDialog,
    EmailDialog,
    NameDialog,
    NhsNumberDialog,
    PhoneDialog,
} from "@/features/profile/components/EditProfileDialogs";
import type { ProfileT } from "@patient-watch/types/database";

import { useAuth } from "@/utils/auth";
import { createFileRoute, Link } from "@tanstack/react-router";
import { CreditCard, Lock, LogOut, ShieldCheck } from "lucide-react";
import { useDemo } from "../../../hooks/useDemo";
import DarkModeSwitch from "../../auth/components/DarkModeSwitch";
import SuperUserSwitch from "../../auth/components/SuperUserSwitch";
import PreferencesCard from "../../knock/components/PreferencesCard";
import useUsersOrganisationsRolesQuery from "@/features/auth/api/selectUsersOrganisationsRoles";
import { ProfileAccessDebug } from "./ProfileAccessDebug";
import { LanguageSwitcher } from "@/features/i18n/LanguageSwitcher";
import { useAppLocale } from "@/features/i18n/LocaleProvider";
import { useProfileCopy } from "@/features/i18n/profileCopy";
import { useAuthCopy } from "@/features/i18n/authCopy";

interface Props {
    profile: ProfileT | null;
}
const Profile = ({ profile }: Props) => {
    const auth = useAuth();
    const { locale } = useAppLocale();
    const copy = useProfileCopy();
    const authCopy = useAuthCopy();
    const isDemo = useDemo();
    const logout = useLogoutMutation();
    const { data: queriedOrganisationRoles, isSuccess: organisationRolesLoaded } = useUsersOrganisationsRolesQuery(
        auth.id
    );
    const usersOrganisationsRoles = organisationRolesLoaded
        ? (queriedOrganisationRoles ?? [])
        : (auth.roles?.usersOrganisationsRoles ?? []);
    const organisationCount = new Set(usersOrganisationsRoles.map((row) => row.organisation_id).filter(Boolean)).size;
    // A patient in a single organisation has nothing to choose or join.
    const hideOrganisations = Boolean(auth.roles?.isPatientOnly) && organisationCount === 1;

    // Hidden from Profile UI (task: Remove Advanced from Profile). Keep component for later.
    const showAccessDebug = false;

    return (
        <>
            <cc.Group title={copy.contactDetails}>
                <NameDialog profile={profile} />
                <DateOfBirthDialog profile={profile} show={auth.roles?.isPatient} />
                <EmailDialog profile={profile} />
                <PhoneDialog show={auth.roles?.isPatient || auth.roles?.isAdmin} profile={profile} />
            </cc.Group>

            <cc.Group show={auth.roles?.isClinician} title={copy.hospitalDetails}>
                <ClinicianNumberDialog profile={profile} show={auth.roles?.isClinician} />
                <NhsNumberDialog profile={profile} show={false} />
            </cc.Group>

            {!hideOrganisations && (
                <cc.Group title={copy.myOrganisations}>
                    <pc.OrganisationsRoles usersOrganisationsRoles={usersOrganisationsRoles} />
                    <ui.Button className="flex mx-auto my-2" variant="outline" asChild>
                        <Link data-testid="add-organisation-link" to="/profile/organisation">
                            {copy.joinOrganisation}
                        </Link>
                    </ui.Button>
                </cc.Group>
            )}

            {auth.roles.isAdmin && (
                <cc.Group title="Build your Own">
                    <div className="flex flex-col gap-y-4 my-4">
                        <SuperUserSwitch isSuperUser={profile?.is_super_user ?? false} />
                    </div>
                </cc.Group>
            )}

            <PreferencesCard />

            <cc.Group title={copy.appearance}>
                <DarkModeSwitch />
            </cc.Group>

            <cc.Group title={copy.language}>
                <LanguageSwitcher userId={auth.id as string} currentLocale={locale} />
            </cc.Group>

            {!isDemo && (
                <cc.Group title={copy.otherActions}>
                    {/* <PWAPrompt /> */}
                    {auth.roles?.isClinician && (
                        <Link
                            className="flex items-center gap-x-3 my-4 w-full font-medium text-xl cursor-pointer"
                            to="/profile/billing"
                        >
                            <CreditCard size={32} strokeWidth={1.75} />
                            {copy.billingAndSubscription}
                        </Link>
                    )}
                    <Link
                        className="flex items-center gap-x-3 my-4 w-full font-medium text-xl cursor-pointer"
                        to="/profile/change-password"
                    >
                        <Lock size={32} strokeWidth={1.75} />
                        {copy.changePassword}
                    </Link>
                    {(auth.roles.isAdmin || auth.isSuperUser) && (
                        <Link
                            className="flex items-center gap-x-3 my-4 w-full font-medium text-xl cursor-pointer"
                            to="/profile/mfa"
                        >
                            <ShieldCheck size={32} strokeWidth={1.75} />
                            {authCopy.mfaSettingsTitle}
                        </Link>
                    )}
                    <DeleteUserButton />
                </cc.Group>
            )}

            {showAccessDebug && auth.id ? <ProfileAccessDebug userId={auth.id} /> : null}

            <ui.Button variant="outline" className="flex mx-auto my-2" onClick={() => logout.mutate()}>
                <LogOut size={32} strokeWidth={1.75} />
                {copy.logout}
            </ui.Button>
        </>
    );
};
export const Route = createFileRoute("/_app/profile/")({
    component: Profile,
});
export default Profile;
